import { useTranslation } from 'next-i18next';
import { FC, useCallback, useMemo, useState } from 'react';
import { Element, scroller } from 'react-scroll';
import { Category, Rubric, Rubric as RubricType } from 'shared/api';
import { CONDITION_DESKTOP, CONDITION_MOBILE } from 'shared/common/constants';
import { routes } from 'shared/common/routes';
import { buildRoute } from 'shared/helpers';
import { Button } from 'shared/ui';
import styled from 'styled-components';
import { Modal, OfferRubricForm } from 'widgets/common';
import { MainLayout } from 'widgets/layouts';

import { SearchBar, TopRubric } from './ui';

export interface ISubCategory {
  name: string;
  id: number;
  mainCategory?: string;
}

type TopicsProps = {
  mainRubricCategories: Category[];
  rubrics: RubricType[];
};

const Topics: FC<TopicsProps> = ({ mainRubricCategories, rubrics }) => {
  const { t } = useTranslation('topics.page');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const categoriesForSearch = useMemo(
    () =>
      rubrics?.sort((a, b) => {
        if (a.createdAt === b.createdAt) return 0;
        if (a.createdAt < b.createdAt) return 1;
        return -1;
      }),
    [],
  );

  const allSearchCategories: ISubCategory[] = useMemo(() => getAllSearchCategories(categoriesForSearch), [rubrics]);

  function getAllSearchCategories(categories: Rubric[]) {
    const allSubRubrics: ISubCategory[] = [];
    const uniqNames: string[] = [];
    if (categories && categories?.length) {
      categories.forEach((item) => {
        const mainCategory = item.name;
        item.categories.forEach((item) => {
          if (!uniqNames.includes(item.name)) {
            allSubRubrics.push({
              name: item.name,
              id: item.id,
              mainCategory,
            });
            uniqNames.push(item.name);
          }
          if (item.subCategories && item.subCategories.length) {
            item.subCategories.forEach((item) => {
              if (!uniqNames.includes(item.name)) {
                allSubRubrics.push({
                  name: item.name,
                  id: item.id,
                  mainCategory,
                });
                uniqNames.push(item.name);
              }
            });
          }
        });
      });
    }
    return allSubRubrics;
  }

  const categories = useMemo(() => {
    if (mainRubricCategories) {
      return mainRubricCategories.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
    }
    return [];
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const head = {
    title: t('head.title'),
  };

  const [currentTopRubricName, setCurrentTopRubricName] = useState(categories[0]?.name);

  const rubricsNamesList: { name: string; count: number; isUpcoming?: boolean }[] = rubrics
    .sort((a) => (a.name === 'Бизнес' ? -1 : a.name !== 'Бизнес' ? 1 : 0))
    .map((item) => ({
      name: item.name,
      count: item.categories.reduce((acc, item) => item.expertsCount + acc, 0),
      isUpcoming: item.isUpcoming,
    }));

  const scrollToElementHandler = (elementName: string) => {
    scroller.scrollTo(elementName, {
      duration: 1500,
      delay: 100,
      smooth: 'easeInOutQuint',
      offset: -90,
    });
  };

  const renderMainRubricCategories = useMemo(
    () =>
      rubrics?.map((item, index) => (
        <RubricWrapper name={item.name} key={index}>
          <TopRubric
            title={item.name}
            iconUrl={item.iconUrl}
            href={buildRoute(routes.category, { id: item.id })}
            isUpcoming={item.isUpcoming}
            list={item.categories
              .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1))
              .map((category) => ({
                id: category.id,
                title: category.name,
                href: buildRoute(routes.category, { id: category.id }),
                count: category.expertsCount,
                subCategories: category.subCategories,
              }))}
            count={item.categories.reduce((acc, item) => item.expertsCount + acc, 0)}
          />
        </RubricWrapper>
      )),
    [mainRubricCategories],
  );

  return (
    <MainLayout head={head} hasFooter>
      <h1 className="hidden">{t('head.title')}</h1>
      <SearchBarWrapper>
        <SearchBar allSubRubrics={allSearchCategories} />
      </SearchBarWrapper>
      <Content className="container">
        <RubricsContainer>
          <TopicsListContainer>
            {rubricsNamesList.map((item, index) => (
              <TopicItem
                key={index}
                onClick={() => {
                  setCurrentTopRubricName(item.name);
                  scrollToElementHandler(item.name);
                }}
              >
                {currentTopRubricName === item.name ? (
                  <CurrentTopicItemName>{item.name}</CurrentTopicItemName>
                ) : (
                  <TopicItemName>{item.name}</TopicItemName>
                )}
                {currentTopRubricName === item.name ? (
                  <CurrentTopicItemCount>{item.count}</CurrentTopicItemCount>
                ) : (
                  <TopicItemCount>{item.count}</TopicItemCount>
                )}
              </TopicItem>
            ))}
          </TopicsListContainer>
          <RubricsListWrapper>
            <TopRubricsList>{renderMainRubricCategories}</TopRubricsList>
          </RubricsListWrapper>
        </RubricsContainer>

        <ProposeRubric>
          <ProposeIcon
            src="/img/think-for-banner.png"
            srcSet="/img/think-for-banner@2x.png 2x, /img/think-for-banner@3x.png 3x"
          />
          <ProposeText>
            <ProposeTitle>{t('propose.title')}</ProposeTitle>
            <ProposeDescr>{t('propose.description')}</ProposeDescr>
          </ProposeText>
          <Button bordered color="white" onClick={openModal}>
            {t('propose.button')}
          </Button>
          <Modal isVisible={isModalOpen} onClose={closeModal}>
            <OfferRubricForm />
          </Modal>
        </ProposeRubric>
      </Content>
    </MainLayout>
  );
};

export default Topics;

const Content = styled.main``;

const RubricWrapper = styled(Element)`
  &:not(:first-child) {
    margin-top: 90px;
  }

  ${CONDITION_MOBILE} {
    &:not(:first-child) {
      margin-top: 0px;
    }
  }
`;

const TopRubricsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  margin: 60px 0 0;

  ${CONDITION_DESKTOP} {
    flex-direction: column;
    margin: 60px 0 0;
    margin-top: 0;
  }

  ${CONDITION_MOBILE} {
    margin-top: 40px;
  }
`;

const ProposeRubric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  margin-bottom: 46px;
  padding: 30px 20px 40px;
  border-radius: 12px;
  color: var(--white);
  background-image: var(--gradient2);

  ${CONDITION_DESKTOP} {
    flex-direction: row;
    padding: 25px 60px 25px 35px;
  }
`;

const ProposeIcon = styled.img`
  width: 70px;
`;

const ProposeText = styled.div`
  text-align: center;

  ${CONDITION_DESKTOP} {
    text-align: left;
    flex-grow: 1;
    margin-left: 25px;
  }
`;

const ProposeTitle = styled.h3`
  font-weight: bold;
  font-size: 21px;
  margin: 20px 0 15px;

  ${CONDITION_DESKTOP} {
    margin: 0 0 9px;
  }
`;

const ProposeDescr = styled.p`
  font-weight: 300;
  font-size: 16px;
  margin-bottom: 30px;

  ${CONDITION_DESKTOP} {
    margin: 0;
  }
`;

const SearchBarWrapper = styled.div`
  margin-bottom: 60px;

  @media (max-width: 400px) {
    width: 100vw;
    margin-bottom: 20px;
  }
`;

const RubricsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 20px;

  ${CONDITION_MOBILE} {
    flex-direction: column;
    margin-left: 0;
  }
`;

const TopicsListContainer = styled.div`
  width: 280px;
  border-radius: 12px;
  padding: 25px 20px 15px 25px;
  background-color: #f7f6ff;
  overflow-y: scroll;

  ${CONDITION_DESKTOP} {
    position: sticky;
    z-index: 997;
    top: 90px;
    max-height: 700px;
  }

  ${CONDITION_MOBILE} {
    width: 100%;
    font-size: 15px;
    font-weight: 500;
  }

  ::-webkit-scrollbar {
    display: none;
  }
`;

const RubricsListWrapper = styled.div`
  margin-left: 110px;
  max-width: 650px;

  ${CONDITION_MOBILE} {
    margin-left: 0;
  }
`;

const TopicItem = styled.div`
  font-size: 15px;
  font-weight: 500;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 10px;

  &:hover {
    cursor: pointer;
    color: #8b33ff;
  }
`;

const TopicItemName = styled.div``;

const CurrentTopicItemName = styled.div``;

const TopicItemCount = styled.div`
  color: #b1b1b1;
`;

const CurrentTopicItemCount = styled.div`
  color: #b1b1b1;
`;
